import { clsx } from "clsx";
import { Button } from "../../../../components/common/button";
import { Image } from "../../../../components/common/image";
import { Text } from "../../../../components/common/typography";
import type { SavedComparisonDto } from "../../data/comparison.types";
import { IconFileDescription } from "@tabler/icons-react";

interface ComparisonListItemProps {
  comparison: SavedComparisonDto;
  onView: (comparison: SavedComparisonDto) => void;
  className?: string;
}

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

function formatCreatedOn(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `Created on ${DATE_FORMATTER.format(date)}`;
}

export function ComparisonListItem({ comparison, onView, className }: ComparisonListItemProps) {
  const itemCount = comparison.variantIds.length;

  const handleClick = () => onView(comparison);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onView(comparison);
    }
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`View ${comparison.name}`}
      className={clsx(
        "flex w-full cursor-pointer items-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-6 transition-colors max-md:flex-wrap max-md:p-4",
        "shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
        "hover:border-borderDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accentCyan",
        className,
      )}
    >
      <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-bgLight">
        {comparison.thumbnailUrl ? (
          <Image
            image={comparison.thumbnailUrl}
            alt=""
            size="size-14"
            objectFit="cover"
          />
        ) : (
          <IconFileDescription size={28} className="text-textMuted" />
        )}
      </div>

      {/* Phones: name fills the first row beside the thumbnail; item count
          and View drop to a second row aligned under the name. */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 max-md:basis-[calc(100%-68px)]">
        <Text variant="bodyBold" tone="dark" className="truncate">
          {comparison.name}
        </Text>
        <Text variant="bodyXs" tone="muted">
          {formatCreatedOn(comparison.createdAt)}
        </Text>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 max-md:pl-[68px]">
        <Text variant="bodyXs" tone="muted">
          Items
        </Text>
        <Text variant="bodyBold" tone="dark">
          {itemCount} {itemCount === 1 ? "product" : "products"}
        </Text>
      </div>

      <Button
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onView(comparison);
        }}
        aria-label={`View ${comparison.name}`}
      >
        View
      </Button>
    </article>
  );
}
